import { motion } from "framer-motion";
import { BookOpen, Eye, Palette, Code2 } from "lucide-react";

const groups = [
  {
    title: "Publishing",
    icon: BookOpen,
    color: "#1F5EFF",
    items: ["Adobe InDesign", "EPUB3", "HTML5", "XML / DTD", "MathML", "Kindle KF8"],
  },
  {
    title: "Accessibility",
    icon: Eye,
    color: "#00B8A9",
    items: ["WCAG 2.2", "PDF/UA", "JAWS", "NVDA", "ARIA", "A11Y Audits"],
  },
  {
    title: "Graphics",
    icon: Palette,
    color: "#6D28D9",
    items: ["Photoshop", "Illustrator", "SVG", "Scientific Artwork", "Cover Design", "Infographics"],
  },
  {
    title: "Development",
    icon: Code2,
    color: "#F59E0B",
    items: ["JavaScript", "CSS", "HTML5 Interactive", "Git", "Automation Pipelines", "OCR"],
  },
];

export default function TechStack() {
  return (
    <section data-testid="techstack-section" className="relative py-24 sm:py-28 bg-[#0B1F3A] text-white overflow-hidden">
      <div className="absolute -top-32 -left-32 h-[420px] w-[420px] rounded-full bg-[#1F5EFF]/30 blur-[120px]" />
      <div className="absolute -bottom-32 -right-32 h-[420px] w-[420px] rounded-full bg-[#6D28D9]/25 blur-[120px]" />

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-6">
            <div className="overline text-[#00B8A9]">Technology &amp; Innovation</div>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter leading-[1.08]">
              Powered by industry-leading publishing technologies.
            </h2>
          </div>
          <div className="lg:col-span-6">
            <p className="text-white/70 text-base sm:text-lg leading-relaxed">
              We leverage modern publishing platforms, accessibility tools, automation frameworks, and structured
              content technologies to streamline production, enhance quality, and accelerate content delivery
              across print and digital ecosystems.
            </p>
          </div>
        </div>

        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {groups.map((g, gi) => {
            const Icon = g.icon;
            return (
              <motion.div
                key={g.title}
                data-testid={`tech-group-${g.title.toLowerCase()}`}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: gi * 0.08 }}
                className="rounded-3xl border border-white/10 bg-white/5 backdrop-blur-sm p-6 hover:bg-white/10 hover:border-white/20 transition-colors"
              >
                <div className="flex items-center gap-3 mb-5">
                  <span
                    className="inline-flex h-10 w-10 items-center justify-center rounded-xl"
                    style={{ backgroundColor: `${g.color}22`, color: g.color }}
                  >
                    <Icon className="h-5 w-5" />
                  </span>
                  <h3 className="font-display font-bold text-lg tracking-tight">{g.title}</h3>
                </div>
                <div className="flex flex-wrap gap-2">
                  {g.items.map((s) => (
                    <span
                      key={s}
                      className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-[12px] font-medium text-white/90"
                    >
                      <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: g.color }} />
                      {s}
                    </span>
                  ))}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
